<script setup lang="ts">
import BaseButton from '@/common/components/molecules/baseButton/BaseButton.vue';
import FeaturedIcon from '@/common/components/molecules/featuredIcon/FeaturedIcon.vue';
import SimpleTextTooltip from '@/common/components/molecules/simpleTextTooltip/SimpleTextTooltip.vue';
import PopupListElement from '../popupListElement/PopupListElement.vue';
import { type Props, type Emit, setup } from './menuElement.setup';

const props = defineProps<Props>();
const emit = defineEmits<Emit>();

const {
  favoriteRef,

  isCurrentPath,
  isDashboardMenu,
  isFavoriteIconVisible,
  isFavorited,
  isAnalysisFavorited,

  childLength,

  tooltipMouseEvent,

  onHover,
  onClick,
  onMouseEventOnText,
} = setup(props, emit);
</script>

<template>
  <popup-list-element
    :selected="isCurrentPath"
    :class="['floating-menu-element', { clicked: props.clicked, 'is-dashboard': isDashboardMenu }]"
    html-type="button"
    @hover="onHover"
    @click="onClick"
  >
    <template #left-side>
      <featured-icon
        v-if="props.menuInfo.featuredIcon"
        :icon-code="props.menuInfo.featuredIcon"
      />
      <ev-icon
        v-else-if="!props.menuInfo.featuredIcon && isFavoriteIconVisible"
        icon="icon-dashboard"
        size="small"
      />

      <span
        @mouseenter="onMouseEventOnText"
        @mouseleave="onMouseEventOnText"
      >
        {{ props.menuInfo.text }}
      </span>
    </template>
    <template #default>
      <base-button
        v-if="isFavoriteIconVisible"
        ref="favoriteRef"
        tag="button"
        :class="['favorite-button', { active: isFavorited || isAnalysisFavorited }]"
      >
        <ev-icon :icon="isFavorited || isAnalysisFavorited ? 'icon-fill-star' : 'icon-star'" />
      </base-button>
      <ev-icon
        v-else-if="childLength > 0"
        class="floating-menu-element__chevron"
        icon="icon-arrow-right"
      />
    </template>
  </popup-list-element>
  <simple-text-tooltip
    :mouse-event="tooltipMouseEvent"
    base-pos="top"
    custom-class="floating-menu-element__tooltip"
    :text="props.menuInfo.text"
  />
</template>

<style scoped lang="scss">
// GNB 2Depth 플라이아웃 메뉴명은 말줄임 대신 최대 2줄까지 노출한다.
// (예: 일본어 'ネットワークパフォーマンスモニタリング' 등 긴 라벨 잘림 방지)
// 단, 서브메뉴의 대시보드 항목(.is-dashboard)은 기존대로 1줄 말줄임 + hover 툴팁을 유지한다.
// 1줄 말줄임 기본값의 소유자는 전역 `src/common/styles/layout/gnb.scss` 의
// `.floating-menu-element .left-side > span` 이다. 그쪽을 수정할 때 이 오버라이드도 함께 확인할 것.
// 전역이 아니라 scoped 로 덮는 이유는 `.is-dashboard` 가 없는 DashboardElement 까지 2줄이 되는 것을
// 막기 위함이다 (DECISIONS.md 2026-08-05 참고).
.floating-menu-element:not(.is-dashboard) {
  height: auto;
  min-height: 32px;

  :deep(.left-side) {
    height: auto;
    min-height: 18px;

    // 텍스트 라벨(.left-side 직속 span)만 대상 — 향후 슬롯에 추가되는 span 래퍼는 비대상
    > span {
      @include shortening($line: 2);
    }
  }
}

.favorite-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  cursor: pointer;
  border-radius: 2px;
  background-color: transparent;

  i {
    width: 16px;
    max-width: 16px;
    height: 16px;
    max-height: 16px;
    background-color: var(--color-gray-03-09);
    mask-size: 18px;
  }

  &.active i {
    background-color: var(--color-yellow-05-05);
  }

  &:hover {
    background-color: var(--color-gray-05-03);
  }
}
</style>

<style lang="scss">
.text-tooltip.floating-menu-element__tooltip {
  pointer-events: none;
  background-color: var(--color-gray-05-12);
  color: var(--color-gray-00-00);

  &.show-tail.top::before {
    border-top-color: var(--color-gray-05-12);
  }
}
</style>
